/**
 * EPC Modal Component
 *
 * Handles EPC certificate management for properties:
 * - Finding EPCs from UK Government database
 * - Uploading manual EPC certificates
 * - Removing attached EPCs
 *
 * Registered globally for Turbo Frame compatibility.
 */
import { fetchAndReplaceSmoothly } from '../utils/content_transitions'

export function epcModal() {
  return {
    activeTab: 'find',
    confirmRemove: false,
    isSearching: false,
    isUploading: false,
    aiAnalysing: false,
    aiMessage: '',
    searchResults: [],
    searchMessage: '',
    searchError: false,
    uploadMessage: '',
    uploadError: false,
    uploadedFile: null,
    epcForm: {
      certificate_number: '',
      date_issued: '',
      date_expires: '',
      current_energy_score: '',
      current_energy_rating: '',
      potential_energy_score: '',
      potential_energy_rating: ''
    },

    // Initialize and watch for modal opening to reset state
    init() {
      this.$watch('$store.modals.isEpcModalOpen', (isOpen) => {
        if (isOpen) {
          this.resetModalState();
        }
      });
    },

    // Reset all local state when modal opens for a new property
    resetModalState() {
      this.activeTab = 'find';
      this.confirmRemove = false;
      this.isSearching = false;
      this.isUploading = false;
      this.aiAnalysing = false;
      this.aiMessage = '';
      this.searchResults = [];
      this.searchMessage = '';
      this.searchError = false;
      this.uploadMessage = '';
      this.uploadError = false;
      this.uploadedFile = null;
      this.resetForm();
    },

    // Get property ID from store
    get propertyId() {
      return Alpine.store('modals').epcModalPropertyId;
    },

    // Show toast notification
    showNotification(message, type = 'success') {
      const alertClass = type === 'success'
        ? 'bg-green-100 border-green-400 text-green-700'
        : 'bg-red-100 border-red-400 text-red-700';

      const flashContainer = document.getElementById('flash');
      if (!flashContainer) return;

      const notification = document.createElement('div');
      notification.className = `fixed top-20 z-99999 border-l-4 p-4 rounded shadow-lg w-full md:max-w-md md:right-4 md:left-auto md:transform-none left-1/2 transform -translate-x-1/2 md:-translate-x-0 ${alertClass}`;
      notification.style.top = '5rem';
      notification.innerHTML = `<p class="text-sm font-medium">${message}</p>`;

      flashContainer.appendChild(notification);

      // Auto-remove after 4 seconds
      setTimeout(() => {
        notification.style.transition = 'opacity 300ms ease-in';
        notification.style.opacity = '0';
        setTimeout(() => notification.remove(), 300);
      }, 4000);
    },

    // Refresh just the EPC card via targeted fetch with smooth transition
    async refreshEpcCard() {
      try {
        await fetchAndReplaceSmoothly(
          `/properties/${this.propertyId}/epc/card`,
          '#property-epc-sidebar',
          { spring: true }
        );
      } catch (error) {
        console.error('Failed to refresh EPC card:', error);
      }
    },

    // Update the certificates card with new/updated EPC data
    updateCertificatesCard(epcData, action = 'create') {
      // Find the certificates component
      const certificatesCard = document.querySelector('[x-data="propertyCertificates"]');
      if (!certificatesCard || !certificatesCard._x_dataStack) return;

      const component = certificatesCard._x_dataStack[0];
      if (!component || !component.certificates) return;

      if (action === 'create' || action === 'update') {
        // Find existing EPC entry (if updating) or add new one
        const existingIndex = component.certificates.findIndex(c => c.is_epc);

        if (existingIndex !== -1) {
          // Update existing EPC entry
          component.certificates[existingIndex] = epcData;
        } else {
          // Add new EPC entry
          component.certificates.push(epcData);
        }

        // Re-sort by expiry date descending
        component.certificates.sort((a, b) => new Date(b.expiry_date) - new Date(a.expiry_date));
      } else if (action === 'destroy') {
        // Remove EPC entry
        component.certificates = component.certificates.filter(c => !c.is_epc);
      }
    },

    // Close modal and refresh targeted areas
    closeAndRefresh(message, epcData = null, action = 'create') {
      Alpine.store('modals').isEpcModalOpen = false;
      this.showNotification(message, 'success');

      // Clear stored EPC data so reopening the modal won't show stale data
      if (action === 'destroy') {
        Alpine.store('modals').epcModalCurrentEpc = null;
      } else if (epcData) {
        Alpine.store('modals').epcModalCurrentEpc = epcData;
      }

      // Notify property slideover to update its cached EPC data
      window.dispatchEvent(new CustomEvent('epc-updated', {
        detail: { propertyId: this.propertyId, action, epcData }
      }));

      // Refresh just the EPC card (smooth transition)
      this.refreshEpcCard();

      // Update certificates card data directly (instant, no flicker)
      if (epcData) {
        this.updateCertificatesCard(epcData, action);
      } else if (action === 'destroy') {
        this.updateCertificatesCard(null, 'destroy');
      }
    },

    // Format expiry date for display
    formatExpiryDate(epc) {
      if (!epc || !epc.date_expires) return '';
      const expiryDate = new Date(epc.date_expires);
      const today = new Date();
      const isExpired = expiryDate < today;
      const formatted = this.formatDate(epc.date_expires);
      return isExpired ? `Expired ${formatted}` : `Valid until ${formatted}`;
    },

    findEpc() {
      this.isSearching = true;
      this.searchMessage = '';
      this.searchError = false;
      this.searchResults = [];

      fetch(`/properties/${this.propertyId}/epc/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      .then(response => {
        return response.json();
      })
      .then(data => {
        this.isSearching = false;
        if (data.success) {
          this.searchResults = data.results || [];
          if (this.searchResults.length === 0) {
            this.searchMessage = 'No EPC certificates found for this property';
            this.searchError = true;
          } else {
            this.searchMessage = `Found ${this.searchResults.length} certificate${this.searchResults.length === 1 ? '' : 's'}`;
            this.searchError = false;
          }
        } else {
          this.searchMessage = data.error || 'Search failed';
          this.searchError = true;
        }
      })
      .catch(error => {
        this.isSearching = false;
        this.searchMessage = 'Search failed';
        this.searchError = true;
        console.error('EPC search error:', error);
      });
    },

    isCurrentEpc(epc) {
      const currentEpc = Alpine.store('modals').epcModalCurrentEpc;
      if (!currentEpc) return false;

      return (epc.certificate_number && epc.certificate_number === currentEpc.certificate_number);
    },

    selectEpc(epc) {
      if (this.isCurrentEpc(epc)) return; // Don't allow attaching if already current

      fetch(`/properties/${this.propertyId}/epc/attach`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ epc: epc })
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.closeAndRefresh('EPC certificate attached successfully', data.epc, 'update');
        } else {
          this.showNotification('Failed to attach EPC: ' + (data.error || 'Unknown error'), 'error');
        }
      })
      .catch(error => {
        console.error('EPC attach error:', error);
        this.showNotification('Failed to attach EPC', 'error');
      });
    },

    removeEpc() {
      fetch(`/properties/${this.propertyId}/epc/detach`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.closeAndRefresh('EPC certificate removed successfully', null, 'destroy');
        } else {
          this.showNotification('Failed to remove EPC: ' + (data.error || 'Unknown error'), 'error');
        }
      })
      .catch(error => {
        console.error('EPC remove error:', error);
        this.showNotification('Failed to remove EPC', 'error');
      });
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    validateAndSetFile(file) {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert('File size must be less than 10MB');
        return;
      }
      this.uploadedFile = file;
      this.uploadMessage = '';
      this.uploadError = false;

      // Kick off AI extraction to pre-fill the form. Non-blocking: if it fails
      // or is unsure, the user simply fills the fields in by hand.
      this.analyseFile(file);
    },

    // Send the uploaded PDF to Claude for field extraction and pre-fill the form.
    async analyseFile(file) {
      this.aiAnalysing = true;
      this.aiMessage = 'Reading certificate…';

      try {
        const formData = new FormData();
        formData.append('certificate', file);

        const response = await fetch(`/properties/${this.propertyId}/epc/analyse`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: formData
        });

        // Guard against a stale response after the user removed/replaced the file
        if (this.uploadedFile !== file) return;

        if (!response.ok) {
          this.aiMessage = '';
          return; // Fall back to manual entry
        }

        const { extracted } = await response.json();

        if (this.uploadedFile !== file) return; // Stale
        if (!extracted) { this.aiMessage = ''; return; }

        // Don't prefill from a non-EPC or a low-confidence read
        if (extracted.is_epc === false || extracted.confidence === 'low') {
          this.aiMessage = extracted.is_epc === false
            ? "This doesn't look like an EPC — please check the fields below."
            : '';
          return;
        }

        if (extracted.certificate_number) {
          this.epcForm.certificate_number = extracted.certificate_number;
        }

        if (extracted.current_energy_score) {
          this.epcForm.current_energy_score = String(extracted.current_energy_score);
          this.calculateCurrentRating();
        }
        if (extracted.potential_energy_score) {
          this.epcForm.potential_energy_score = String(extracted.potential_energy_score);
          this.calculatePotentialRating();
        }

        // Dates: set the issue date, then trust the AI's expiry only if it's
        // roughly 10 years later (EPC validity). Otherwise auto-calculate it.
        if (extracted.issue_date) {
          this.setDateField('epc_date_issued', 'date_issued', extracted.issue_date);
        }

        let expiryAccepted = false;
        if (extracted.expiry_date && extracted.issue_date) {
          const issue = new Date(extracted.issue_date);
          const expiry = new Date(extracted.expiry_date);
          const yearsAfter = (expiry - issue) / (1000 * 60 * 60 * 24 * 365.25);
          if (yearsAfter >= 8 && yearsAfter <= 11) {
            this.setDateField('epc_date_expires', 'date_expires', extracted.expiry_date);
            expiryAccepted = true;
          }
        }
        if (!expiryAccepted && this.epcForm.date_issued) {
          this.calculateExpiryDate();
          this.setDateField('epc_date_expires', 'date_expires', this.epcForm.date_expires);
        }

        this.aiMessage = 'We filled in the details below — please double-check them.';
      } catch (error) {
        console.error('EPC analysis error:', error);
        this.aiMessage = ''; // Silent fallback to manual entry
      } finally {
        if (this.uploadedFile === file) this.aiAnalysing = false;
      }
    },

    // Set an epcForm date field and push the value into its flatpickr instance
    // so the visible input updates too.
    setDateField(inputId, formKey, value) {
      this.epcForm[formKey] = value;
      this.$nextTick(() => {
        const input = document.getElementById(inputId);
        if (input && input._flatpickr) {
          input._flatpickr.setDate(value, false);
        }
      });
    },

    resetForm() {
      this.epcForm = {
        certificate_number: '',
        date_issued: '',
        date_expires: '',
        current_energy_score: '',
        current_energy_rating: '',
        potential_energy_score: '',
        potential_energy_rating: ''
      };

      // Clear flatpickr instances so they don't show stale dates
      this.$nextTick(() => {
        this.$el?.querySelectorAll('[data-controller="flatpickr"]').forEach(input => {
          if (input._flatpickr) {
            input._flatpickr.clear();
          }
        });
        // Clear the file input so re-selecting the same file triggers change
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      });
    },

    calculateExpiryDate() {
      if (this.epcForm.date_issued) {
        const issueDate = new Date(this.epcForm.date_issued);
        const expiryDate = new Date(issueDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + 10);
        expiryDate.setDate(expiryDate.getDate() - 1); // 10 years minus 1 day
        this.epcForm.date_expires = expiryDate.toISOString().split('T')[0];
      }
    },

    calculateRatingFromScore(score) {
      const numScore = parseInt(score);
      if (numScore >= 92) return 'A';
      if (numScore >= 81) return 'B';
      if (numScore >= 69) return 'C';
      if (numScore >= 55) return 'D';
      if (numScore >= 39) return 'E';
      if (numScore >= 21) return 'F';
      return 'G';
    },

    calculateCurrentRating() {
      if (this.epcForm.current_energy_score) {
        this.epcForm.current_energy_rating = this.calculateRatingFromScore(this.epcForm.current_energy_score);
      } else {
        this.epcForm.current_energy_rating = '';
      }
    },

    calculatePotentialRating() {
      if (this.epcForm.potential_energy_score) {
        this.epcForm.potential_energy_rating = this.calculateRatingFromScore(this.epcForm.potential_energy_score);
      } else {
        this.epcForm.potential_energy_rating = '';
      }
    },

    isFormValid() {
      return this.uploadedFile &&
             this.epcForm.certificate_number &&
             this.epcForm.date_issued &&
             this.epcForm.date_expires &&
             this.epcForm.current_energy_score &&
             this.epcForm.potential_energy_score &&
             parseInt(this.epcForm.current_energy_score) >= 1 &&
             parseInt(this.epcForm.current_energy_score) <= 100 &&
             parseInt(this.epcForm.potential_energy_score) >= 1 &&
             parseInt(this.epcForm.potential_energy_score) <= 100;
    },

    uploadEpc() {
      if (!this.isFormValid()) return;

      this.isUploading = true;
      this.uploadMessage = '';
      this.uploadError = false;

      const formData = new FormData();
      formData.append('certificate', this.uploadedFile);
      formData.append('certificate_number', this.epcForm.certificate_number);
      formData.append('date_issued', this.epcForm.date_issued);
      formData.append('date_expires', this.epcForm.date_expires);
      formData.append('current_energy_score', this.epcForm.current_energy_score);
      formData.append('current_energy_rating', this.epcForm.current_energy_rating);
      formData.append('potential_energy_score', this.epcForm.potential_energy_score);
      formData.append('potential_energy_rating', this.epcForm.potential_energy_rating);

      fetch(`/properties/${this.propertyId}/epc/upload`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        this.isUploading = false;
        if (data.success) {
          this.closeAndRefresh('EPC certificate uploaded successfully', data.epc, 'update');
        } else {
          this.uploadMessage = data.error || 'Upload failed';
          this.uploadError = true;
          this.showNotification(data.error || 'Upload failed', 'error');
        }
      })
      .catch(error => {
        this.isUploading = false;
        this.uploadMessage = 'Upload failed';
        this.uploadError = true;
        this.showNotification('Upload failed', 'error');
        console.error('EPC upload error:', error);
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Unknown';
        return date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      } catch (e) {
        console.error('Date formatting error:', e, dateString);
        return 'Unknown';
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '';
      const mb = bytes / (1024 * 1024);
      return mb.toFixed(1) + ' MB';
    }
  }
}
